/**
 * Travel Assistant Quick Peek Hook
 *
 * Fetches quick peek data (5 sections) and caches each in React Query cache by (location, sessionId, userId).
 * Use getCachedQuickPeek(request) to read merged cache before fetching for instant Travel Guide on reopen.
 */

import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchQuickPeekTravelPlanning,
  fetchQuickPeekCulturalInfo,
  fetchQuickPeekActivitiesFacilities,
  fetchQuickPeek,
} from '@/services/travelAssistantService';
import {
  QuickPeekRequest,
  QuickPeekResponse,
} from '@/services/api/interfaces/ITravelAssistantRepository';
import { useTravelPlanningOnly } from './useTravelPlanningOnly';
import { useNavigationAccessibility } from './useNavigationAccessibility';
import { useCulturalSocial } from './useCulturalSocial';
import { useHistoricalSouvenirs } from './useHistoricalSouvenirs';

const QUICK_PEEK_KEY_PREFIX = ['travelAssistant', 'quickPeek'] as const;
const QUICK_PEEK_SECTIONS = [
  'travelPlanningOnly',
  'navigationAccessibility',
  'culturalSocial',
  'historicalSouvenirs',
  'activitiesFacilities',
] as const;

/** Normalize request for stable cache keys so read/write always match. */
function normalizeQuickPeekRequest(request: QuickPeekRequest): {
  location: string;
  session_id: string;
  user_id: string;
} {
  return {
    location: String(request?.location ?? '').trim(),
    session_id: String(request?.session_id ?? '').trim(),
    user_id: String(request?.user_id ?? '').trim(),
  };
}

function quickPeekSectionKey(
  request: QuickPeekRequest,
  section: (typeof QUICK_PEEK_SECTIONS)[number]
): unknown[] {
  const n = normalizeQuickPeekRequest(request);
  return [
    ...QUICK_PEEK_KEY_PREFIX,
    section,
    n.location,
    n.session_id,
    n.user_id,
  ];
}

/** Single key for the full merged Quick Peek response (flat array for reliable matching). */
function quickPeekMergedKey(request: QuickPeekRequest): unknown[] {
  const n = normalizeQuickPeekRequest(request);
  return [
    ...QUICK_PEEK_KEY_PREFIX,
    'merged',
    n.location,
    n.session_id,
    n.user_id,
  ];
}

export interface UseQuickPeekOptions {
  accessToken?: string;
  refreshToken?: string;
}

export interface UseQuickPeekResult {
  quickPeek: QuickPeekResponse | null;
  isLoading: boolean;
  error: Error | null;
  fetchQuickPeek: (
    request: QuickPeekRequest,
    signal?: AbortSignal
  ) => Promise<QuickPeekResponse | null>;
  clearError: () => void;
  getCachedQuickPeek: (request: QuickPeekRequest) => QuickPeekResponse | null;
  setCachedQuickPeek: (
    request: QuickPeekRequest,
    response: QuickPeekResponse | null
  ) => void;
  // Separate API calls (deprecated - use individual hooks instead)
  travelPlanning: Partial<QuickPeekResponse> | null;
  culturalInfo: Partial<QuickPeekResponse> | null;
  activitiesFacilities: Partial<QuickPeekResponse> | null;
  isLoadingTravelPlanning: boolean;
  isLoadingCulturalInfo: boolean;
  isLoadingActivitiesFacilities: boolean;
  errorTravelPlanning: Error | null;
  errorCulturalInfo: Error | null;
  errorActivitiesFacilities: Error | null;
  fetchTravelPlanning: (
    request: QuickPeekRequest,
    signal?: AbortSignal
  ) => Promise<Partial<QuickPeekResponse> | null>;
  fetchCulturalInfo: (
    request: QuickPeekRequest
  ) => Promise<Partial<QuickPeekResponse> | null>;
  fetchActivitiesFacilities: (
    request: QuickPeekRequest,
    signal?: AbortSignal
  ) => Promise<Partial<QuickPeekResponse> | null>;
  // New separate hooks for travel planning split
  travelPlanningOnly: Partial<QuickPeekResponse> | null;
  navigationAccessibility: Partial<QuickPeekResponse> | null;
  isLoadingTravelPlanningOnly: boolean;
  isLoadingNavigationAccessibility: boolean;
  errorTravelPlanningOnly: Error | null;
  errorNavigationAccessibility: Error | null;
  fetchTravelPlanningOnly: (
    request: QuickPeekRequest
  ) => Promise<Partial<QuickPeekResponse> | null>;
  fetchNavigationAccessibility: (
    request: QuickPeekRequest
  ) => Promise<Partial<QuickPeekResponse> | null>;
  // Parallel fetch for both travel planning endpoints
  fetchTravelPlanningParallel: (
    request: QuickPeekRequest,
    signal?: AbortSignal
  ) => Promise<{
    travelPlanningOnly: Partial<QuickPeekResponse> | null;
    navigationAccessibility: Partial<QuickPeekResponse> | null;
  }>;
  // New separate hooks for cultural split
  culturalSocial: Partial<QuickPeekResponse> | null;
  historicalSouvenirs: Partial<QuickPeekResponse> | null;
  isLoadingCulturalSocial: boolean;
  isLoadingHistoricalSouvenirs: boolean;
  errorCulturalSocial: Error | null;
  errorHistoricalSouvenirs: Error | null;
  fetchCulturalSocial: (
    request: QuickPeekRequest
  ) => Promise<Partial<QuickPeekResponse> | null>;
  fetchHistoricalSouvenirs: (
    request: QuickPeekRequest
  ) => Promise<Partial<QuickPeekResponse> | null>;
  // Parallel fetch for both cultural endpoints
  fetchCulturalParallel: (
    request: QuickPeekRequest,
    signal?: AbortSignal
  ) => Promise<{
    culturalSocial: Partial<QuickPeekResponse> | null;
    historicalSouvenirs: Partial<QuickPeekResponse> | null;
  }>;
}

/**
 * Hook for fetching travel assistant quick peek using React Query
 * Uses mutation pattern for manual triggering with caching
 * Now supports 3 separate API calls that update UI progressively
 *
 * @param options - Optional configuration including access and refresh tokens
 * @returns Object containing quick peek data, loading state, error, and fetch function
 */
export function useQuickPeek(
  options?: UseQuickPeekOptions
): UseQuickPeekResult {
  const queryClient = useQueryClient();

  // Composition: Use separate hooks for travel planning split
  // Dependency Inversion: Depends on hook abstractions
  const travelPlanningOnlyHook = useTravelPlanningOnly(options);
  const navigationAccessibilityHook = useNavigationAccessibility(options);

  // Composition: Use separate hooks for cultural split
  // Dependency Inversion: Depends on hook abstractions
  const culturalSocialMutation = useCulturalSocial(
    options?.accessToken,
    options?.refreshToken
  );
  const historicalSouvenirsMutation = useHistoricalSouvenirs(
    options?.accessToken,
    options?.refreshToken
  );

  // Legacy merged API mutation (deprecated)
  const mutation = useMutation({
    mutationFn: async ({
      request,
      signal,
    }: {
      request: QuickPeekRequest;
      signal?: AbortSignal;
    }) => {
      return fetchQuickPeek(
        request,
        options?.accessToken,
        options?.refreshToken,
        signal
      );
    },
    onSuccess: (data, variables) => {
      console.log('✅ [useQuickPeek] Data fetched successfully (no caching):', {
        hasData: !!data,
        location: variables.request.location,
        dataKeys: data ? Object.keys(data) : [],
      });
    },
    retry: (failureCount, error) => {
      // Don't retry if the request was canceled
      if (
        (error as Error & { code?: string })?.code === 'ERR_CANCELED' ||
        (error as Error & { name?: string })?.name === 'CanceledError' ||
        (error as Error).message === 'canceled'
      ) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Travel Planning API mutation
  const travelPlanningMutation = useMutation({
    mutationFn: async ({
      request,
      signal,
    }: {
      request: QuickPeekRequest;
      signal?: AbortSignal;
    }) => {
      return fetchQuickPeekTravelPlanning(
        request,
        options?.accessToken,
        options?.refreshToken,
        signal
      );
    },
    onSuccess: (data, variables) => {
      console.log('✅ [useQuickPeek] Travel Planning fetched:', {
        hasData: !!data,
        location: variables.request.location,
        dataKeys: data ? Object.keys(data) : [],
      });
    },
    retry: (failureCount, error) => {
      // Don't retry if the request was canceled
      if (
        (error as Error & { code?: string })?.code === 'ERR_CANCELED' ||
        (error as Error & { name?: string })?.name === 'CanceledError' ||
        (error as Error).message === 'canceled'
      ) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Cultural Info API mutation
  const culturalInfoMutation = useMutation({
    mutationFn: async ({
      request,
      signal,
    }: {
      request: QuickPeekRequest;
      signal?: AbortSignal;
    }) => {
      return fetchQuickPeekCulturalInfo(
        request,
        options?.accessToken,
        options?.refreshToken,
        signal
      );
    },
    onSuccess: (data, variables) => {
      console.log('✅ [useQuickPeek] Cultural Info fetched:', {
        hasData: !!data,
        location: variables.request.location,
        dataKeys: data ? Object.keys(data) : [],
      });
    },
    retry: (failureCount, error) => {
      // Don't retry if the request was canceled
      if (
        (error as Error & { code?: string })?.code === 'ERR_CANCELED' ||
        (error as Error & { name?: string })?.name === 'CanceledError' ||
        (error as Error).message === 'canceled'
      ) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Activities & Facilities API mutation
  const activitiesFacilitiesMutation = useMutation({
    mutationFn: async ({
      request,
      signal,
    }: {
      request: QuickPeekRequest;
      signal?: AbortSignal;
    }) => {
      return fetchQuickPeekActivitiesFacilities(
        request,
        options?.accessToken,
        options?.refreshToken,
        signal
      );
    },
    onSuccess: (data, variables) => {
      console.log('✅ [useQuickPeek] Activities & Facilities fetched:', {
        hasData: !!data,
        location: variables.request.location,
        dataKeys: data ? Object.keys(data) : [],
      });
    },
    retry: (failureCount, error) => {
      // Don't retry if the request was canceled
      if (
        (error as Error & { code?: string })?.code === 'ERR_CANCELED' ||
        (error as Error & { name?: string })?.name === 'CanceledError' ||
        (error as Error).message === 'canceled'
      ) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Legacy fetch function
  const fetchQuickPeekData = async (
    request: QuickPeekRequest,
    signal?: AbortSignal
  ): Promise<QuickPeekResponse | null> => {
    try {
      const response = await mutation.mutateAsync({ request, signal });
      return response;
    } catch (err) {
      // Ignore cancellation errors
      if (
        (err as Error & { code?: string })?.code === 'ERR_CANCELED' ||
        (err as Error & { name?: string })?.name === 'CanceledError' ||
        (err as Error).message === 'canceled'
      ) {
        console.log('🛑 [useQuickPeek] fetchQuickPeek cancelled');
        return null;
      }

      const error =
        err instanceof Error ? err : new Error('Failed to fetch quick peek');
      console.error('Error fetching quick peek:', error);
      return null;
    }
  };

  // Separate fetch functions
  const fetchTravelPlanningData = async (
    request: QuickPeekRequest,
    signal?: AbortSignal
  ): Promise<Partial<QuickPeekResponse> | null> => {
    try {
      const response = await travelPlanningMutation.mutateAsync({
        request,
        signal,
      });
      return response;
    } catch (err) {
      // Ignore cancellation errors
      if (
        (err as Error & { code?: string })?.code === 'ERR_CANCELED' ||
        (err as Error & { name?: string })?.name === 'CanceledError' ||
        (err as Error).message === 'canceled'
      ) {
        console.log('🛑 [useQuickPeek] fetchTravelPlanningData cancelled');
        return null;
      }

      const error =
        err instanceof Error
          ? err
          : new Error('Failed to fetch travel planning');
      console.error('Error fetching travel planning:', error);
      return null;
    }
  };

  const fetchCulturalInfoData = async (
    request: QuickPeekRequest,
    signal?: AbortSignal
  ): Promise<Partial<QuickPeekResponse> | null> => {
    try {
      const response = await culturalInfoMutation.mutateAsync({
        request,
        signal,
      });
      return response;
    } catch (err) {
      // Ignore cancellation errors
      if (
        (err as Error & { code?: string })?.code === 'ERR_CANCELED' ||
        (err as Error & { name?: string })?.name === 'CanceledError' ||
        (err as Error).message === 'canceled'
      ) {
        console.log('🛑 [useQuickPeek] fetchCulturalInfoData cancelled');
        return null;
      }

      const error =
        err instanceof Error ? err : new Error('Failed to fetch cultural info');
      console.error('Error fetching cultural info:', error);
      return null;
    }
  };

  const fetchActivitiesFacilitiesData = async (
    request: QuickPeekRequest,
    signal?: AbortSignal
  ): Promise<Partial<QuickPeekResponse> | null> => {
    try {
      const response = await activitiesFacilitiesMutation.mutateAsync({
        request,
        signal,
      });
      if (response) {
        queryClient.setQueryData(
          quickPeekSectionKey(request, 'activitiesFacilities'),
          response
        );
      }
      return response;
    } catch (err) {
      // Ignore cancellation errors
      if (
        (err as Error & { code?: string })?.code === 'ERR_CANCELED' ||
        (err as Error & { name?: string })?.name === 'CanceledError' ||
        (err as Error).message === 'canceled'
      ) {
        console.log(
          '🛑 [useQuickPeek] fetchActivitiesFacilitiesData cancelled'
        );
        return null;
      }

      const error =
        err instanceof Error
          ? err
          : new Error('Failed to fetch activities & facilities');
      console.error('Error fetching activities & facilities:', error);
      return null;
    }
  };

  // Parallel fetch for both travel planning endpoints
  // Calls both APIs simultaneously and returns results in order
  // Handles partial failures gracefully - if one API fails, the other still succeeds
  const fetchTravelPlanningParallelData = async (
    request: QuickPeekRequest,
    signal?: AbortSignal
  ): Promise<{
    travelPlanningOnly: Partial<QuickPeekResponse> | null;
    navigationAccessibility: Partial<QuickPeekResponse> | null;
  }> => {
    // Use Promise.allSettled to handle partial failures gracefully
    // This ensures one API failure doesn't prevent the other from succeeding
    const [travelPlanningOnlyResult, navigationAccessibilityResult] =
      await Promise.allSettled([
        travelPlanningOnlyHook.fetch(request, signal),
        navigationAccessibilityHook.fetch(request, signal),
      ]);

    // Extract results, maintaining order
    // Handle each result independently - one failure doesn't affect the other
    let travelPlanningOnly: Partial<QuickPeekResponse> | null = null;
    if (travelPlanningOnlyResult.status === 'fulfilled') {
      travelPlanningOnly = travelPlanningOnlyResult.value;
    } else {
      console.error(
        '❌ [useQuickPeek] Travel Planning Only API failed:',
        travelPlanningOnlyResult.reason
      );
    }

    let navigationAccessibility: Partial<QuickPeekResponse> | null = null;
    if (navigationAccessibilityResult.status === 'fulfilled') {
      navigationAccessibility = navigationAccessibilityResult.value;
    } else {
      console.error(
        '❌ [useQuickPeek] Navigation Accessibility API failed:',
        navigationAccessibilityResult.reason
      );
    }

    // Cache each section for instant display on reopen
    if (travelPlanningOnly) {
      queryClient.setQueryData(
        quickPeekSectionKey(request, 'travelPlanningOnly'),
        travelPlanningOnly
      );
    }
    if (navigationAccessibility) {
      queryClient.setQueryData(
        quickPeekSectionKey(request, 'navigationAccessibility'),
        navigationAccessibility
      );
    }

    return {
      travelPlanningOnly,
      navigationAccessibility,
    };
  };

  // Fetch functions for cultural split
  const fetchCulturalSocialData = async (
    request: QuickPeekRequest,
    signal?: AbortSignal
  ): Promise<Partial<QuickPeekResponse> | null> => {
    try {
      const response = await culturalSocialMutation.mutateAsync({
        request,
        signal,
      });
      return response;
    } catch (err) {
      // Ignore cancellation errors
      if (
        (err as Error & { code?: string })?.code === 'ERR_CANCELED' ||
        (err as Error & { name?: string })?.name === 'CanceledError' ||
        (err as Error).message === 'canceled'
      ) {
        console.log('🛑 [useQuickPeek] fetchCulturalSocialData cancelled');
        return null;
      }

      const error =
        err instanceof Error
          ? err
          : new Error('Failed to fetch cultural social');
      console.error('Error fetching cultural social:', error);
      return null;
    }
  };

  const fetchHistoricalSouvenirsData = async (
    request: QuickPeekRequest,
    signal?: AbortSignal
  ): Promise<Partial<QuickPeekResponse> | null> => {
    try {
      const response = await historicalSouvenirsMutation.mutateAsync({
        request,
        signal,
      });
      return response;
    } catch (err) {
      // Ignore cancellation errors
      if (
        (err as Error & { code?: string })?.code === 'ERR_CANCELED' ||
        (err as Error & { name?: string })?.name === 'CanceledError' ||
        (err as Error).message === 'canceled'
      ) {
        console.log('🛑 [useQuickPeek] fetchHistoricalSouvenirsData cancelled');
        return null;
      }

      const error =
        err instanceof Error
          ? err
          : new Error('Failed to fetch historical souvenirs');
      console.error('Error fetching historical souvenirs:', error);
      return null;
    }
  };

  // Parallel fetch for both cultural endpoints
  // Calls both APIs simultaneously and returns results in order
  // Handles partial failures gracefully - if one API fails, the other still succeeds
  const fetchCulturalParallelData = async (
    request: QuickPeekRequest,
    signal?: AbortSignal
  ): Promise<{
    culturalSocial: Partial<QuickPeekResponse> | null;
    historicalSouvenirs: Partial<QuickPeekResponse> | null;
  }> => {
    // Use Promise.allSettled to handle partial failures gracefully
    // This ensures one API failure doesn't prevent the other from succeeding
    const [culturalSocialResult, historicalSouvenirsResult] =
      await Promise.allSettled([
        fetchCulturalSocialData(request, signal),
        fetchHistoricalSouvenirsData(request, signal),
      ]);

    // Extract results, maintaining order
    // Handle each result independently - one failure doesn't affect the other
    let culturalSocial: Partial<QuickPeekResponse> | null = null;
    if (culturalSocialResult.status === 'fulfilled') {
      culturalSocial = culturalSocialResult.value;
    } else {
      console.error(
        '❌ [useQuickPeek] Cultural Social API failed:',
        culturalSocialResult.reason
      );
    }

    let historicalSouvenirs: Partial<QuickPeekResponse> | null = null;
    if (historicalSouvenirsResult.status === 'fulfilled') {
      historicalSouvenirs = historicalSouvenirsResult.value;
    } else {
      console.error(
        '❌ [useQuickPeek] Historical Souvenirs API failed:',
        historicalSouvenirsResult.reason
      );
    }

    // Cache each section for instant display on reopen
    if (culturalSocial) {
      queryClient.setQueryData(
        quickPeekSectionKey(request, 'culturalSocial'),
        culturalSocial
      );
    }
    if (historicalSouvenirs) {
      queryClient.setQueryData(
        quickPeekSectionKey(request, 'historicalSouvenirs'),
        historicalSouvenirs
      );
    }

    return {
      culturalSocial,
      historicalSouvenirs,
    };
  };

  const clearError = () => {
    mutation.reset();
    travelPlanningMutation.reset();
    culturalInfoMutation.reset();
    activitiesFacilitiesMutation.reset();
    travelPlanningOnlyHook.reset();
    navigationAccessibilityHook.reset();
  };

  const getCachedQuickPeek = (
    request: QuickPeekRequest
  ): QuickPeekResponse | null => {
    // Prefer single merged entry so we always get the full store state as cached
    const mergedCached = queryClient.getQueryData<QuickPeekResponse>(
      quickPeekMergedKey(request)
    );
    if (mergedCached && typeof mergedCached === 'object') {
      return mergedCached;
    }
    // Fallback: merge from per-section keys (from hook writes during fetch)
    let merged: QuickPeekResponse | null = null;
    for (const section of QUICK_PEEK_SECTIONS) {
      const data = queryClient.getQueryData<Partial<QuickPeekResponse>>(
        quickPeekSectionKey(request, section)
      );
      if (data && typeof data === 'object') {
        merged = merged ? { ...merged, ...data } : { ...data };
      }
    }
    return merged;
  };

  // Cache the complete merged response for instant retrieval (single key = reliable on reopen)
  const setCachedQuickPeek = (
    request: QuickPeekRequest,
    response: QuickPeekResponse | null
  ): void => {
    if (!response || typeof response !== 'object') return;
    queryClient.setQueryData(quickPeekMergedKey(request), response);
  };

  const isLoading =
    mutation.isPending ||
    travelPlanningMutation.isPending ||
    culturalInfoMutation.isPending ||
    activitiesFacilitiesMutation.isPending ||
    travelPlanningOnlyHook.isLoading ||
    navigationAccessibilityHook.isLoading;

  // Log loading state changes
  React.useEffect(() => {
    console.log('🔄 [useQuickPeek] Loading state changed:', {
      isLoading,
      travelPlanning: travelPlanningMutation.isPending,
      culturalInfo: culturalInfoMutation.isPending,
      activitiesFacilities: activitiesFacilitiesMutation.isPending,
      travelPlanningOnly: travelPlanningOnlyHook.isLoading,
      navigationAccessibility: navigationAccessibilityHook.isLoading,
      timestamp: new Date().toISOString(),
    });
  }, [
    isLoading,
    travelPlanningMutation.isPending,
    culturalInfoMutation.isPending,
    activitiesFacilitiesMutation.isPending,
    travelPlanningOnlyHook.isLoading,
    navigationAccessibilityHook.isLoading,
  ]);

  return {
    quickPeek: mutation.data || null,
    isLoading,
    error: mutation.error as Error | null,
    fetchQuickPeek: fetchQuickPeekData,
    clearError,
    getCachedQuickPeek,
    setCachedQuickPeek, // Add this to cache complete merged response
    // Separate API data (deprecated)
    travelPlanning: travelPlanningMutation.data || null,
    culturalInfo: culturalInfoMutation.data || null,
    activitiesFacilities: activitiesFacilitiesMutation.data || null,
    isLoadingTravelPlanning: travelPlanningMutation.isPending,
    isLoadingCulturalInfo: culturalInfoMutation.isPending,
    isLoadingActivitiesFacilities: activitiesFacilitiesMutation.isPending,
    errorTravelPlanning: travelPlanningMutation.error as Error | null,
    errorCulturalInfo: culturalInfoMutation.error as Error | null,
    errorActivitiesFacilities:
      activitiesFacilitiesMutation.error as Error | null,
    fetchTravelPlanning: fetchTravelPlanningData,
    fetchCulturalInfo: fetchCulturalInfoData,
    fetchActivitiesFacilities: fetchActivitiesFacilitiesData,
    // New separate hooks for travel planning split
    travelPlanningOnly: travelPlanningOnlyHook.data,
    navigationAccessibility: navigationAccessibilityHook.data,
    isLoadingTravelPlanningOnly: travelPlanningOnlyHook.isLoading,
    isLoadingNavigationAccessibility: navigationAccessibilityHook.isLoading,
    errorTravelPlanningOnly: travelPlanningOnlyHook.error,
    errorNavigationAccessibility: navigationAccessibilityHook.error,
    fetchTravelPlanningOnly: travelPlanningOnlyHook.fetch,
    fetchNavigationAccessibility: navigationAccessibilityHook.fetch,
    fetchTravelPlanningParallel: fetchTravelPlanningParallelData,
    // Cultural split hooks
    culturalSocial: culturalSocialMutation.data || null,
    historicalSouvenirs: historicalSouvenirsMutation.data || null,
    isLoadingCulturalSocial: culturalSocialMutation.isPending,
    isLoadingHistoricalSouvenirs: historicalSouvenirsMutation.isPending,
    errorCulturalSocial: (culturalSocialMutation.error as Error) || null,
    errorHistoricalSouvenirs:
      (historicalSouvenirsMutation.error as Error) || null,
    fetchCulturalSocial: fetchCulturalSocialData,
    fetchHistoricalSouvenirs: fetchHistoricalSouvenirsData,
    fetchCulturalParallel: fetchCulturalParallelData,
  };
}
